/* ============================================================
   LEVA PROJECT STORE (universal Firestore sync layer)
   assets/leva-project-store.js
   ============================================================
   Single new file — zero changes to existing configurators.

   What it does:
     1. Resolves a stable identity (Shopify customerId or anon uuid).
     2. On load, fetches the user's main project from Firestore
        (via the leva-scrape worker) and reconciles it with the
        local foundations ProjectStore payload in localStorage.
        Remote newer → localStorage updated (+ one guarded reload
        when the configurator already booted with stale state).
        Local newer → pushed to Firestore.
     3. Hooks BaseLib.ProjectStore.publishNow (foundations) so every
        local save is mirrored to Firestore, debounced.
     4. Hooks MasterCanvasBridge.publishQuick/publishFull and syncs
        slim representations (footprint SVG + bbox + budget, never
        GLB data URLs) under modules.repr_<handle>.
     5. Exposes window.LevaProjectStore API:
        load(), saveModule(configKey, payload), setLocation(loc),
        setPublish(enabled), getIdentity(), getProjectId(),
        getMasterFootprint(), diagnose().

   All Firestore writes go through the worker (service-account auth
   on levavendordb) — the browser never talks to Firestore directly.
   ============================================================ */

(function (global) {
  'use strict';

  if (global.LevaProjectStore) {
    return;
  }

  var VERSION = 'lps-v6';
  var WORKER_BASE = 'https://leva-scrape.long-cloud-d006.workers.dev';
  var SAVE_DEBOUNCE_MS = 2500;
  var POLL_MS = 250;
  var POLL_MAX_TRIES = 80;
  var REPR_SYNC_INTERVAL_MS = 30000;
  var MAX_PAYLOAD_BYTES = 250000;
  var MAX_STRING_LEN = 50000;
  var REMOTE_NEWER_SLACK_MS = 2000;

  var ANON_IDENTITY_KEY = 'leva_anon_identity';
  var RELOAD_GUARD_KEY = 'leva_lps_reloaded';
  var REPR_PREFIX = 'mc:representation:';
  var BASE_CONFIG_KEY = 'base';

  /* Foundations and pool share the BaseLib engine; the page's own
     ProjectStore declares which module it persists via CONFIG_KEY
     ('base' on foundations, 'pool' on the pool configurator). */
  function pageConfigKey() {
    var ps = global.BaseLib && global.BaseLib.ProjectStore;
    if (ps && ps.CONFIG_KEY) return ps.CONFIG_KEY;
    var h = String((global.BASE_BOOT && global.BASE_BOOT.productHandle) || '');
    if (h.indexOf('pool') === 0) return 'pool';
    if (h.indexOf('shelter') === 0 || h.indexOf('carport') === 0) return 'shelters';
    if (h.indexOf('structure') === 0) return 'structure';
    if (h.indexOf('roof') === 0) return 'roof';
    return BASE_CONFIG_KEY;
  }
  var AR_WORKER_FALLBACK = 'https://soft-art-55b9.long-cloud-d006.workers.dev';
  var GLB_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;

  var _pending = {};
  var _timers = {};
  var _lastReprSync = {};
  var _lastGlbUpload = {};
  var _glbInFlight = {};
  var _lastGlbResult = null;
  var _canvasHydrated = false;
  var _lastSaveResult = null;
  var _lastLoadResult = null;
  var _remoteProject = null;
  var _remoteProjectId = null;
  var _loadPromise = null;
  var _baseHooked = false;
  var _bridgeHooked = false;

  function dlog() {
    var args = ['[LevaPS]'].concat([].slice.call(arguments));
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      try {
        var msg = args.map(function (a) {
          if (typeof a === 'object') {
            try { return JSON.stringify(a); } catch (e) { return String(a); }
          }
          return String(a);
        }).join(' ');
        global.Configurator.log.log(msg);
      } catch (e) {}
    }
    try { console.log.apply(console, args); } catch (e) {}
  }

  function uuid() {
    if (global.crypto && global.crypto.randomUUID) {
      try { return global.crypto.randomUUID(); } catch (e) {}
    }
    return 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* ============================================================
     Identity
     ============================================================
     Firestore identity: real customerId when logged in, otherwise
     a persistent anonymous uuid. The LOCAL storage-key customer
     segment mirrors BaseLib.ProjectStore.getCustomerId() exactly
     (customerId or the literal 'anon') so we read/write the same
     localStorage keys the foundations lib uses.
     ============================================================ */
  function localCustomerKey() {
    var boot = global.BASE_BOOT || {};
    if (boot.customerId != null) return String(boot.customerId);
    if (global.customerId != null && String(global.customerId) !== '' &&
        String(global.customerId) !== 'null' && String(global.customerId) !== 'undefined') {
      return String(global.customerId);
    }
    return 'anon';
  }

  function getIdentity() {
    var cid = null;
    if (global.BASE_BOOT && global.BASE_BOOT.customerId != null) {
      cid = String(global.BASE_BOOT.customerId);
    } else if (global.customerId != null && String(global.customerId) !== '') {
      cid = String(global.customerId);
    }
    if (cid && cid !== 'null' && cid !== 'undefined' && cid !== 'NaN' && cid !== '') {
      return cid;
    }
    try {
      var v = localStorage.getItem(ANON_IDENTITY_KEY);
      if (!v) {
        v = 'anon-' + uuid();
        localStorage.setItem(ANON_IDENTITY_KEY, v);
      }
      return v;
    } catch (e) {
      return 'anon-session';
    }
  }

  function activeProjectKey() {
    return 'levaActiveProject:c' + localCustomerKey();
  }

  function payloadKey(projectId, configKey) {
    return 'levaProject:p' + projectId + ':c' + localCustomerKey() + ':' + configKey;
  }

  function getProjectId() {
    var ps = global.BaseLib && global.BaseLib.ProjectStore;
    if (ps && typeof ps.getProjectId === 'function') {
      var pid = ps.getProjectId();
      if (pid) return pid;
    }
    try {
      var stored = localStorage.getItem(activeProjectKey());
      if (stored) return stored;
      var fresh = uuid();
      localStorage.setItem(activeProjectKey(), fresh);
      return fresh;
    } catch (e) {
      return 'p-volatile';
    }
  }

  function readLocalBasePayload() {
    var ps = global.BaseLib && global.BaseLib.ProjectStore;
    if (ps && typeof ps.read === 'function') {
      try {
        var data = ps.read(ps.CONFIG_KEY || BASE_CONFIG_KEY);
        if (data) return data;
      } catch (e) {}
    }
    try {
      var raw = localStorage.getItem(payloadKey(getProjectId(), pageConfigKey()));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* ============================================================
     Payload sanitizer — drops oversized strings, data URLs and
     glbDataUrl so binaries never travel to Firestore.
     ============================================================ */
  function sanitize(value, depth) {
    if (depth > 10) return undefined;
    if (value === null) return null;
    var t = typeof value;
    if (t === 'string') {
      if (value.length > MAX_STRING_LEN) return undefined;
      if (value.indexOf('data:') === 0 && value.length > 10000) return undefined;
      return value;
    }
    if (t === 'number') return isFinite(value) ? value : undefined;
    if (t === 'boolean') return value;
    if (Array.isArray(value)) {
      var arr = [];
      for (var i = 0; i < value.length; i++) {
        var av = sanitize(value[i], depth + 1);
        if (av !== undefined) arr.push(av);
      }
      return arr;
    }
    if (t === 'object') {
      var out = {};
      for (var k in value) {
        if (!Object.prototype.hasOwnProperty.call(value, k)) continue;
        if (k === 'glbDataUrl') continue;
        var sv = sanitize(value[k], depth + 1);
        if (sv !== undefined) out[k] = sv;
      }
      return out;
    }
    return undefined;
  }

  /* ============================================================
     GLB → R2 upload (reuses the LEVA AR worker: GET /upload-url,
     then PUT raw GLB to the returned uploadUrl). The public R2
     URL replaces multi-MB data URLs in Firestore and feeds the
     master canvas <model-viewer> directly.
     ============================================================ */
  function arWorkerBase() {
    var boot = global.BASE_BOOT || {};
    var t = boot.i18n || {};
    return boot.arWorkerUrl || t.arWorkerUrl || AR_WORKER_FALLBACK;
  }

  function dataUrlToBytes(dataUrl) {
    var comma = dataUrl.indexOf(',');
    if (comma < 0) return null;
    var meta = dataUrl.slice(0, comma);
    if (meta.indexOf('base64') === -1) return null;
    try {
      var bin = atob(dataUrl.slice(comma + 1));
      var bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes;
    } catch (e) { return null; }
  }

  function uploadGlbBytes(bytes) {
    var base = arWorkerBase();
    return fetch(base + '/upload-url')
      .then(function (r) {
        if (!r.ok) throw new Error('upload-url HTTP ' + r.status);
        return r.json();
      })
      .then(function (info) {
        if (!info || !info.uploadUrl || !info.publicUrl) {
          throw new Error('upload-url response invalid');
        }
        return fetch(info.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'model/gltf-binary' },
          body: bytes
        }).then(function (r2) {
          if (!r2.ok) throw new Error('upload HTTP ' + r2.status);
          return info.publicUrl;
        });
      });
  }

  function uploadGlbToR2(glbDataUrl) {
    var bytes = dataUrlToBytes(glbDataUrl);
    if (!bytes) return Promise.reject(new Error('glb decode failed'));
    if (bytes.length > GLB_UPLOAD_MAX_BYTES) {
      return Promise.reject(new Error('glb too large: ' + bytes.length + 'b'));
    }
    return uploadGlbBytes(bytes);
  }

  /* Public entry for configurator libs: hand over a freshly exported
     GLB ArrayBuffer; the store uploads it to R2 and stamps the public
     glbUrl into the canvas representation + the cloud repr module.
     Binaries never transit localStorage (quota safety). */
  function publishGlbBuffer(handle, buf) {
    try {
      if (!handle || !/^[A-Za-z0-9_-]{1,40}$/.test(String(handle))) {
        return Promise.reject(new Error('publishGlb: bad handle'));
      }
      var bytes = (buf instanceof Uint8Array) ? buf
        : (buf instanceof ArrayBuffer) ? new Uint8Array(buf)
        : null;
      if (!bytes || !bytes.length) return Promise.reject(new Error('publishGlb: expected ArrayBuffer'));
      if (bytes.length > GLB_UPLOAD_MAX_BYTES) {
        return Promise.reject(new Error('publishGlb: glb too large: ' + bytes.length + 'b'));
      }
      var stamp = 'len' + bytes.length;
      if (_glbInFlight[handle]) return Promise.reject(new Error('publishGlb: upload in flight'));
      if (_lastGlbUpload[handle] === stamp) return Promise.resolve(null);  /* unchanged since last upload */
      _glbInFlight[handle] = true;
      return uploadGlbBytes(bytes).then(function (publicUrl) {
        _glbInFlight[handle] = false;
        _lastGlbUpload[handle] = stamp;
        _lastGlbResult = { handle: handle, url: publicUrl };
        dlog('GLB published to R2:', handle, publicUrl);
        var key = REPR_PREFIX + handle;
        var cur = null;
        try {
          var raw = localStorage.getItem(key);
          cur = raw ? JSON.parse(raw) : null;
        } catch (e) {}
        cur = (cur && typeof cur === 'object') ? cur : {};
        cur.glbUrl = publicUrl;
        try { localStorage.setItem(key, JSON.stringify(cur)); } catch (e2) {}
        try { dispatchReprEvent(key, JSON.stringify(cur)); } catch (e3) {}
        queueSave('repr_' + handle, {
          footprint2D: (typeof cur.footprint2D === 'string' && cur.footprint2D.length < MAX_STRING_LEN) ? cur.footprint2D : null,
          bbox: cur.bbox || null,
          budget: cur.budget || null,
          map: cur.map || null,
          glbUrl: publicUrl,
          updatedAt: cur.updatedAt || new Date().toISOString()
        });
        return publicUrl;
      }).catch(function (e) {
        _glbInFlight[handle] = false;
        _lastGlbResult = { handle: handle, error: String(e && e.message ? e.message : e) };
        throw e;
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  function maybeUploadGlb(handle, key, data) {
    if (!data || typeof data.glbDataUrl !== 'string' || data.glbDataUrl.indexOf('data:') !== 0) return;
    var stamp = (data.updatedAt || '') + ':' + data.glbDataUrl.length;
    if (_lastGlbUpload[handle] === stamp || _glbInFlight[handle]) return;
    _glbInFlight[handle] = true;
    uploadGlbToR2(data.glbDataUrl).then(function (publicUrl) {
      _lastGlbUpload[handle] = stamp;
      _glbInFlight[handle] = false;
      _lastGlbResult = { handle: handle, url: publicUrl };
      dlog('GLB uploaded to R2:', handle, publicUrl);
      try {
        var raw = localStorage.getItem(key);
        var cur = raw ? JSON.parse(raw) : null;
        if (cur) {
          cur.glbUrl = publicUrl;
          localStorage.setItem(key, JSON.stringify(cur));
        }
      } catch (e) {}
      queueSave('repr_' + handle, {
        footprint2D: (typeof data.footprint2D === 'string' && data.footprint2D.length < MAX_STRING_LEN) ? data.footprint2D : null,
        bbox: data.bbox || null,
        budget: data.budget || null,
        glbUrl: publicUrl,
        updatedAt: data.updatedAt || new Date().toISOString()
      });
    }).catch(function (e) {
      _glbInFlight[handle] = false;
      _lastGlbResult = { handle: handle, error: String(e && e.message ? e.message : e) };
      dlog('GLB upload FAILED:', handle, e && e.message ? e.message : e);
    });
  }

  /* ============================================================
     Foundations representation synthesis — the bridge does not
     register the base configurator, so the store generates the
     canvas representation itself from the base payload: real
     polygon footprint SVG (not just a bbox rectangle) + budget.
     ============================================================ */
  function polygonToSvg(polygon) {
    if (!Array.isArray(polygon) || polygon.length < 3) return null;
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    var i, p;
    for (i = 0; i < polygon.length; i++) {
      p = polygon[i];
      if (!Array.isArray(p) || p.length < 2) return null;
      if (p[0] < minX) minX = p[0];
      if (p[0] > maxX) maxX = p[0];
      if (p[1] < minY) minY = p[1];
      if (p[1] > maxY) maxY = p[1];
    }
    var px = 100;
    var w = Math.max(20, Math.round((maxX - minX) * px));
    var h = Math.max(20, Math.round((maxY - minY) * px));
    var pts = [];
    for (i = 0; i < polygon.length; i++) {
      p = polygon[i];
      pts.push(Math.round((p[0] - minX) * px) + ',' + Math.round((p[1] - minY) * px));
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + w + ' ' + h + '" width="' + w + '" height="' + h + '">' +
      '<polygon points="' + pts.join(' ') + '" fill="rgba(90,107,71,0.18)" stroke="#5A6B47" stroke-width="2"/>' +
      '</svg>';
  }

  function payloadToRepresentation(payload) {
    if (!payload || !payload.shape) return null;
    var totals = payload.totals || {};
    var mat = +totals.materialKc || 0;
    var exec = +totals.executionKc || 0;
    return {
      footprint2D: polygonToSvg(payload.shape.polygon),
      bbox: {
        width: +payload.shape.bboxW || 0,
        length: +payload.shape.bboxL || 0,
        height: 1
      },
      budget: { materials: mat, execution: exec, total: mat + exec },
      updatedAt: payload.updatedAt || new Date().toISOString()
    };
  }

  function synthesizeBaseRepresentation() {
    /* Synthesize on EVERY configurator page: the repr key comes from
       payload.productHandle, so pool/shelters produce repr_pool /
       repr_shelters here. (The master bridge doesn't run on these
       pages — its registry needs engine globals they don't define —
       so without this, representations freeze after the first save.) */
    if (!isBasePage()) return;
    var payload = readLocalBasePayload();
    if (!payload || !payload.productHandle) return;
    var repr = payloadToRepresentation(payload);
    if (!repr || !repr.footprint2D) return;
    /* Carry the configurator's own map placement so the master canvas
       can anchor the footprint where the user actually put it. */
    if (payload.map && Array.isArray(payload.map.center) && payload.map.center.length >= 2) {
      repr.map = {
        center: [+payload.map.center[0], +payload.map.center[1]],
        bearing: +payload.map.bearing || 0
      };
    }
    var key = REPR_PREFIX + payload.productHandle;
    try {
      var raw = localStorage.getItem(key);
      var cur = raw ? JSON.parse(raw) : null;
      if (cur && cur.updatedAt === repr.updatedAt) return;
      if (cur && cur.glbUrl) repr.glbUrl = cur.glbUrl;
      localStorage.setItem(key, JSON.stringify(repr));
    } catch (e) { return; }
    queueSave('repr_' + payload.productHandle, {
      footprint2D: repr.footprint2D,
      bbox: repr.bbox,
      budget: repr.budget,
      map: repr.map || null,
      glbUrl: repr.glbUrl || null,
      updatedAt: repr.updatedAt
    });
  }

  /* ============================================================
     Master canvas hydration — on the canvas page, merge remote
     repr_* modules into mc:representation:* localStorage, push
     budgets through the public MasterCanvas.publishBudget API and
     fire synthetic storage events so the canvas re-renders itself.
     Zero changes to configurator-master.js.
     ============================================================ */
  function dispatchReprEvent(key, value) {
    try {
      global.dispatchEvent(new StorageEvent('storage', {
        key: key,
        newValue: value,
        storageArea: localStorage
      }));
    } catch (e) {
      try {
        var ev = document.createEvent('Event');
        ev.initEvent('storage', true, true);
        ev.key = key;
        ev.newValue = value;
        global.dispatchEvent(ev);
      } catch (e2) {}
    }
  }

  function hydrateCanvas() {
    if (_canvasHydrated) return;
    if (!global.MasterCanvas || !_remoteProject || !_remoteProject.modules) return;
    _canvasHydrated = true;
    var modules = _remoteProject.modules;
    var touched = 0;

    var reprs = {};
    var k, handle;
    for (k in modules) {
      if (k.indexOf('repr_') === 0 && modules[k]) {
        reprs[k.slice(5)] = modules[k];
      }
    }
    if (modules[BASE_CONFIG_KEY] && modules[BASE_CONFIG_KEY].productHandle) {
      handle = modules[BASE_CONFIG_KEY].productHandle;
      if (!reprs[handle]) {
        var synth = payloadToRepresentation(modules[BASE_CONFIG_KEY]);
        if (synth) reprs[handle] = synth;
      }
    }

    for (handle in reprs) {
      var remote = reprs[handle];
      var key = REPR_PREFIX + handle;
      var local = null;
      try {
        var raw = localStorage.getItem(key);
        if (raw) local = JSON.parse(raw);
      } catch (e) {}
      var rT = Date.parse(remote.updatedAt || 0) || 0;
      var lT = local ? (Date.parse(local.updatedAt || 0) || 0) : 0;
      if (local && lT >= rT) continue;
      var merged = local || {};
      if (remote.footprint2D) merged.footprint2D = remote.footprint2D;
      if (remote.bbox) merged.bbox = remote.bbox;
      if (remote.budget) merged.budget = remote.budget;
      if (remote.map) merged.map = remote.map;
      if (remote.glbUrl) merged.glbDataUrl = remote.glbUrl;
      merged.glbUrl = remote.glbUrl || merged.glbUrl || null;
      merged.updatedAt = remote.updatedAt || new Date().toISOString();
      var serialized;
      try {
        serialized = JSON.stringify(merged);
        localStorage.setItem(key, serialized);
        _lastReprSync[handle] = merged.updatedAt;
      } catch (e) { continue; }
      dispatchReprEvent(key, serialized);
      touched++;
      if (remote.budget && remote.budget.total > 0 &&
          typeof global.MasterCanvas.publishBudget === 'function') {
        try { global.MasterCanvas.publishBudget(handle, remote.budget); } catch (e) {}
      }
    }
    dlog('canvas hydrated from Firestore:', touched + ' representation(s)');
    injectPublishToggle();
  }

  function pollCanvasHydration() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (_canvasHydrated || tries > POLL_MAX_TRIES) { clearInterval(iv); return; }
      if (global.MasterCanvas && _remoteProject) hydrateCanvas();
    }, POLL_MS * 2);
  }

  /* ============================================================
     Project location — fed automatically from addresses the user
     already entered: the foundations map (payload.map) or the
     canvas address modal (mc:state:v1). A signature guard makes
     sure each address is sent only once. The worker computes the
     geohash and activates the anonymous public mirror.
     ============================================================ */
  var LOC_SENT_KEY = 'leva_lps_loc_sent';
  var _canvasAddrAtBoot = null;
  var _canvasAddrCaptured = false;

  function captureCanvasBootAddress() {
    if (_canvasAddrCaptured) return;
    _canvasAddrCaptured = true;
    try {
      var raw = localStorage.getItem('mc:state:v1');
      var st = raw ? JSON.parse(raw) : null;
      _canvasAddrAtBoot = (st && st.address) ? String(st.address) : '';
    } catch (e) { _canvasAddrAtBoot = ''; }
  }

  function maybeSendLocation(lat, lng, label) {
    if (!isFinite(lat) || !isFinite(lng) || !label) return;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    var sig = label + '|' + lat.toFixed(4) + '|' + lng.toFixed(4);
    try { if (localStorage.getItem(LOC_SENT_KEY) === sig) return; } catch (e) {}
    setLocation({ lat: lat, lng: lng, city: label }).then(function (res) {
      if (res && res.ok) {
        try { localStorage.setItem(LOC_SENT_KEY, sig); } catch (e) {}
        dlog('project location updated:', label, '(published=' + res.published + ')');
      }
    });
  }

  function checkLocationSources() {
    try {
      if (isBasePage()) {
        var payload = readLocalBasePayload();
        if (payload && payload.map && payload.map.address &&
            Array.isArray(payload.map.center) && payload.map.center.length >= 2) {
          maybeSendLocation(+payload.map.center[1], +payload.map.center[0], String(payload.map.address));
        }
        return;
      }
      /* Canvas: a persisted address is NOT user intent for this project.
         Only an address changed during this session (via the modal)
         counts. */
      captureCanvasBootAddress();
      var raw = localStorage.getItem('mc:state:v1');
      if (raw) {
        var st = JSON.parse(raw);
        if (st && st.address && String(st.address) !== _canvasAddrAtBoot &&
            isFinite(+st.lat) && isFinite(+st.lng)) {
          maybeSendLocation(+st.lat, +st.lng, String(st.address));
        }
      }
    } catch (e) {}
  }

  /* ============================================================
     Publish opt-out toggle — injected into the canvas drawer
     (all mode) without touching configurator-master.js.
     ============================================================ */
  function injectPublishToggle() {
    if (!global.MasterCanvas || !_remoteProjectId) return;
    if (document.getElementById('lps-publish-row')) return;
    var host = document.getElementById('mc-drawer-all-mode');
    if (!host) return;
    var enabled = !(_remoteProject && _remoteProject.publish && _remoteProject.publish.enabled === false);

    var row = document.createElement('div');
    row.id = 'lps-publish-row';
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:10px;' +
      'padding:12px 2px;margin-top:8px;border-top:1px solid #eee;' +
      'font:600 12px Montserrat,-apple-system,sans-serif;color:#333;';

    var label = document.createElement('span');
    label.textContent = (global.levaLpsI18n && global.levaLpsI18n.shareAnonymously) || 'Share anonymously with visitors nearby';
    label.style.cssText = 'flex:1 1 auto;line-height:1.4;';

    var input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = enabled;
    input.style.cssText = 'width:20px;height:20px;accent-color:#5A6B47;flex:0 0 auto;cursor:pointer;';
    input.addEventListener('change', function () {
      input.disabled = true;
      setPublish(input.checked).then(function (res) {
        input.disabled = false;
        if (!res || !res.ok) input.checked = !input.checked;
        else if (_remoteProject) {
          _remoteProject.publish = { enabled: input.checked };
        }
      });
    });

    row.appendChild(label);
    row.appendChild(input);
    host.appendChild(row);
    dlog('publish toggle injected, enabled=' + enabled);
  }

  /* ============================================================
     Inspiration loader — ?inspire=<projectId>:<key> on a configurator
     page loads that PUBLISHED module (map/notes stripped server-side),
     keeps the visitor's own map section, and reloads once. Requires
     an explicit confirm because it replaces the current setup.
     ============================================================ */
  function writeLocalBasePayload(payload) {
    try {
      payload.updatedAt = new Date().toISOString();
      localStorage.setItem(payloadKey(getProjectId(), pageConfigKey()), JSON.stringify(payload));
      return true;
    } catch (e) { return false; }
  }

  function handleInspireParam() {
    if (!isBasePage()) return;
    var m = /[?&]inspire=([A-Za-z0-9_-]+):([A-Za-z0-9_:-]+)/.exec(global.location.search || '');
    if (!m) return;
    var cleanUrl = global.location.pathname + global.location.search.replace(/[?&]inspire=[^&]+/, '').replace(/^&/, '?') + global.location.hash;
    var srcProjectId = m[1];
    var srcKey = m[2];
    if (srcKey !== pageConfigKey()) {
      dlog('inspire key', srcKey, 'does not match this page (' + pageConfigKey() + ')');
      try { global.history.replaceState(null, '', cleanUrl); } catch (e) {}
      return;
    }
    var msg = (global.levaLpsI18n && global.levaLpsI18n.inspireConfirm) ||
      'Load this shared configuration? Your current setup will be replaced (it stays at your own address).';
    if (!global.confirm(msg)) {
      try { global.history.replaceState(null, '', cleanUrl); } catch (e) {}
      return;
    }
    fetch(WORKER_BASE + '/project/public-module?projectId=' + encodeURIComponent(srcProjectId) +
          '&key=' + encodeURIComponent(srcKey))
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (!res || !res.ok || !res.payload) {
          dlog('inspire load failed:', res && res.error);
          try { global.history.replaceState(null, '', cleanUrl); } catch (e) {}
          return;
        }
        var incoming = res.payload;
        var local = readLocalBasePayload();
        if (local && local.map) incoming.map = local.map;
        else delete incoming.map;
        if (writeLocalBasePayload(incoming)) {
          dlog('inspire payload applied from', srcProjectId);
          global.location.replace(cleanUrl);
        }
      })
      .catch(function (e) { dlog('inspire error:', e && e.message); });
  }

  /* ============================================================
     Remote save (debounced per configKey, beacon flush on pagehide)
     ============================================================ */
  function queueSave(configKey, payload) {
    if (!configKey || !payload) return;
    var cleanPayload = sanitize(payload, 0);
    if (!cleanPayload) return;
    var serialized;
    try { serialized = JSON.stringify(cleanPayload); } catch (e) { return; }
    if (serialized.length > MAX_PAYLOAD_BYTES) {
      dlog('payload too large, skipped:', configKey, serialized.length + 'b');
      return;
    }
    _pending[configKey] = cleanPayload;
    clearTimeout(_timers[configKey]);
    _timers[configKey] = setTimeout(function () { sendSave(configKey); }, SAVE_DEBOUNCE_MS);
  }

  function buildSaveBody(configKey, payload, extra) {
    var body = {
      identity: getIdentity(),
      projectId: getProjectId(),
      configKey: configKey || undefined,
      payload: payload || undefined
    };
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) body[k] = extra[k];
      }
    }
    return body;
  }

  function sendSave(configKey, extra) {
    var payload = _pending[configKey];
    delete _pending[configKey];
    clearTimeout(_timers[configKey]);
    if (!payload && !extra) return Promise.resolve(null);
    var body;
    try { body = JSON.stringify(buildSaveBody(configKey, payload, extra)); } catch (e) { return Promise.resolve(null); }
    return fetch(WORKER_BASE + '/project/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    }).then(function (r) { return r.json(); }).then(function (res) {
      _lastSaveResult = res;
      dlog('save', configKey || '(meta)', res && res.ok ? 'OK' : JSON.stringify(res).slice(0, 200));
      return res;
    }).catch(function (e) {
      _lastSaveResult = { error: String(e && e.message ? e.message : e) };
      dlog('save FAILED', configKey, e && e.message ? e.message : e);
      return null;
    });
  }

  function flushAll() {
    for (var key in _pending) {
      if (!Object.prototype.hasOwnProperty.call(_pending, key)) continue;
      var payload = _pending[key];
      delete _pending[key];
      clearTimeout(_timers[key]);
      var body;
      try { body = JSON.stringify(buildSaveBody(key, payload, null)); } catch (e) { continue; }
      try {
        /* text/plain keeps sendBeacon preflight-free; the worker parses
           the body as JSON regardless of content type. */
        if (navigator.sendBeacon) {
          navigator.sendBeacon(WORKER_BASE + '/project/save', new Blob([body], { type: 'text/plain' }));
        }
      } catch (e) {}
    }
  }

  /* ============================================================
     Remote load + reconcile with the local foundations payload
     ============================================================ */
  function loadRemote() {
    if (_loadPromise) return _loadPromise;
    var url = WORKER_BASE + '/project/load?identity=' + encodeURIComponent(getIdentity());
    _loadPromise = fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (res) {
        _lastLoadResult = res;
        if (res && res.ok) {
          _remoteProject = res.project || null;
          _remoteProjectId = res.projectId || null;
          if (_remoteProjectId) {
            try {
              if (!localStorage.getItem(activeProjectKey())) {
                localStorage.setItem(activeProjectKey(), _remoteProjectId);
                dlog('adopted remote projectId (no local key):', _remoteProjectId);
              }
            } catch (e) {}
          }
          dlog('load OK, remote project:', _remoteProjectId || 'none',
               _remoteProject ? ('modules: ' + Object.keys(_remoteProject.modules || {}).join(',')) : '');
        } else {
          dlog('load returned error:', JSON.stringify(res).slice(0, 200));
        }
        return res;
      })
      .catch(function (e) {
        _lastLoadResult = { error: String(e && e.message ? e.message : e) };
        dlog('load FAILED:', e && e.message ? e.message : e);
        return null;
      });
    return _loadPromise;
  }

  function isBasePage() {
    return !!(global.BASE_BOOT || document.getElementById('cf-wrap-base'));
  }

  function maybeReload() {
    if (!isBasePage()) return;
    if (!global.__BASE_BOOTED__) return;
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
        dlog('remote newer but reload already used this session — applied to storage only');
        return;
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch (e) { return; }
    dlog('remote state newer — reloading once to apply');
    setTimeout(function () { global.location.reload(); }, 150);
  }

  function reconcileBase(res) {
    if (!res || !res.ok || !isBasePage()) return;
    var remoteMod = _remoteProject && _remoteProject.modules && _remoteProject.modules[pageConfigKey()];
    var local = readLocalBasePayload();

    if (!remoteMod) {
      if (local) {
        dlog('no remote base module — first upload of local state');
        queueSave(pageConfigKey(), local);
      }
      return;
    }

    /* Cross-configurator pollution guard: a payload carries the
       productHandle of the page that wrote it. If the remote module
       was written by a different configurator (e.g. pool data saved
       under the base key by a stale lib), never pull it — push the
       local state instead, which repairs the remote copy. */
    var bootHandle = String((global.BASE_BOOT && global.BASE_BOOT.productHandle) || '');
    var remoteHandle = String(remoteMod.productHandle || '');
    if (bootHandle && remoteHandle && remoteHandle !== bootHandle) {
      dlog('remote module belongs to "' + remoteHandle + '" but this page is "' + bootHandle + '" — repairing from local');
      if (local) queueSave(pageConfigKey(), local);
      return;
    }

    var rT = Date.parse(remoteMod.updatedAt || 0) || 0;
    var lT = local ? (Date.parse(local.updatedAt || 0) || 0) : 0;

    if (rT > lT + REMOTE_NEWER_SLACK_MS) {
      var localPid = getProjectId();
      var targetPid = _remoteProjectId || localPid;
      try {
        if (_remoteProjectId && _remoteProjectId !== localPid) {
          localStorage.setItem(activeProjectKey(), _remoteProjectId);
          dlog('adopted remote projectId:', _remoteProjectId);
        }
        localStorage.setItem(payloadKey(targetPid, pageConfigKey()), JSON.stringify(remoteMod));
        dlog('remote base state written to localStorage (remote ' + new Date(rT).toISOString() + ' > local)');
      } catch (e) {
        dlog('localStorage write failed:', e && e.message ? e.message : e);
        return;
      }
      maybeReload();
    } else if (local && lT > rT + REMOTE_NEWER_SLACK_MS) {
      dlog('local base state newer — pushing to Firestore');
      queueSave(pageConfigKey(), local);
    } else {
      dlog('base state in sync');
    }
  }

  /* ============================================================
     Hook 1: BaseLib.ProjectStore.publishNow (foundations)
     ============================================================ */
  function hookBaseStore() {
    if (_baseHooked) return true;
    var ps = global.BaseLib && global.BaseLib.ProjectStore;
    if (!ps || typeof ps.publishNow !== 'function') return false;
    if (ps.publishNow.__lpsHooked) { _baseHooked = true; return true; }
    var orig = ps.publishNow;
    ps.publishNow = function () {
      orig.apply(this, arguments);
      try {
        var data = ps.read(ps.CONFIG_KEY || BASE_CONFIG_KEY);
        if (data) queueSave(ps.CONFIG_KEY || BASE_CONFIG_KEY, data);
        synthesizeBaseRepresentation();
      } catch (e) {}
    };
    ps.publishNow.__lpsHooked = true;
    _baseHooked = true;
    dlog('BaseLib.ProjectStore.publishNow hooked');
    /* The lib may have appeared after the boot-time reconcile ran with
       the handle-based key fallback; reconcile again now that the
       page's real ProjectStore (and CONFIG_KEY) exists. */
    setTimeout(function () {
      loadRemote().then(function (res) { reconcileBase(res); });
    }, 1500);
    return true;
  }

  /* ============================================================
     Hook 2: MasterCanvasBridge representations (slim, no GLB)
     ============================================================ */
  function syncRepresentations() {
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i);
        if (!key || key.indexOf(REPR_PREFIX) !== 0) continue;
        var handle = key.substring(REPR_PREFIX.length);
        if (!/^[A-Za-z0-9_-]{1,40}$/.test(handle)) continue;
        var raw = localStorage.getItem(key);
        if (!raw) continue;
        var data;
        try { data = JSON.parse(raw); } catch (e) { continue; }
        if (!data) continue;
        maybeUploadGlb(handle, key, data);
        var stamp = data.updatedAt ? String(data.updatedAt) : ('len' + raw.length);
        if (_lastReprSync[handle] === stamp) continue;
        _lastReprSync[handle] = stamp;
        var slim = {
          footprint2D: (typeof data.footprint2D === 'string' && data.footprint2D.length < MAX_STRING_LEN) ? data.footprint2D : null,
          bbox: data.bbox || null,
          budget: data.budget || null,
          glbUrl: data.glbUrl || null,
          updatedAt: data.updatedAt || new Date().toISOString()
        };
        queueSave('repr_' + handle, slim);
      }
    } catch (e) {}
  }

  function hookBridge() {
    if (_bridgeHooked) return true;
    var br = global.MasterCanvasBridge;
    if (!br) return false;
    if (br.__lpsHooked) { _bridgeHooked = true; return true; }
    ['publishQuick', 'publishFull'].forEach(function (fn) {
      if (typeof br[fn] !== 'function') return;
      var orig = br[fn];
      br[fn] = function () {
        orig.apply(this, arguments);
        setTimeout(syncRepresentations, 3000);
      };
    });
    br.__lpsHooked = true;
    _bridgeHooked = true;
    dlog('MasterCanvasBridge hooked for representation sync');
    return true;
  }

  /* ============================================================
     Public API
     ============================================================ */
  function setLocation(loc) {
    if (!loc || !isFinite(Number(loc.lat)) || !isFinite(Number(loc.lng))) {
      return Promise.resolve(null);
    }
    return sendSave(null, {
      location: {
        lat: Number(loc.lat),
        lng: Number(loc.lng),
        city: loc.city || '',
        countryCode: loc.countryCode || ''
      }
    });
  }

  function setPublish(enabled) {
    var body = JSON.stringify({
      identity: getIdentity(),
      projectId: getProjectId(),
      enabled: enabled !== false
    });
    return fetch(WORKER_BASE + '/project/publish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    }).then(function (r) { return r.json(); }).then(function (res) {
      dlog('publish toggle:', JSON.stringify(res).slice(0, 200));
      return res;
    }).catch(function (e) {
      dlog('publish toggle FAILED:', e && e.message ? e.message : e);
      return null;
    });
  }

  function getMasterFootprint() {
    if (_remoteProject && _remoteProject.master && Array.isArray(_remoteProject.master.footprint)) {
      return _remoteProject.master.footprint;
    }
    var local = readLocalBasePayload();
    if (local && local.shape && Array.isArray(local.shape.polygon)) {
      return local.shape.polygon;
    }
    return null;
  }

  function diagnose() {
    var info = {
      version: VERSION,
      identity: getIdentity(),
      localCustomerKey: localCustomerKey(),
      projectId: getProjectId(),
      remoteProjectId: _remoteProjectId,
      remoteModules: _remoteProject ? Object.keys(_remoteProject.modules || {}) : null,
      baseHooked: _baseHooked,
      bridgeHooked: _bridgeHooked,
      pendingSaves: Object.keys(_pending),
      canvasPresent: !!global.MasterCanvas,
      canvasHydrated: _canvasHydrated,
      locationSent: (function () { try { return localStorage.getItem(LOC_SENT_KEY); } catch (e) { return null; } })(),
      lastGlbResult: _lastGlbResult,
      arWorker: arWorkerBase(),
      lastSaveResult: _lastSaveResult,
      lastLoadResult: _lastLoadResult ? (_lastLoadResult.ok ? 'ok' : _lastLoadResult) : null,
      localBasePayload: !!readLocalBasePayload()
    };
    dlog('=== DIAGNOSE ===');
    for (var k in info) {
      dlog(k + ':', info[k]);
    }
    dlog('=== /DIAGNOSE ===');
    return info;
  }

  /* ============================================================
     On-screen debug overlay — no DevTools needed.
     Activate by appending #lpsdebug to the configurator URL.
     Shows identity, hooks, payload state and last load/save
     results, plus a FORCE SAVE NOW button.
     ============================================================ */
  function _canvasDiagLine() {
    try {
      if (!global.MasterCanvas && !global.MC_MODULES) return 'canvasDiag: -';
      var handles = [];
      if (Array.isArray(global.MC_MODULES)) {
        for (var i = 0; i < global.MC_MODULES.length; i++) {
          handles.push(String(global.MC_MODULES[i].productHandle || '?'));
        }
      }
      var reprs = [];
      for (var j = 0; j < localStorage.length; j++) {
        var k = localStorage.key(j);
        if (k && k.indexOf('mc:representation:') === 0) {
          var h = k.substring('mc:representation:'.length);
          var info = '';
          try {
            var rp = JSON.parse(localStorage.getItem(k) || 'null') || {};
            var bb = rp.bbox || {};
            var dims = (bb.length && bb.width)
              ? (Number(bb.length).toFixed(1) + 'x' + Number(bb.width).toFixed(1) + 'm')
              : 'nobbox';
            var ts = rp.updatedAt ? String(rp.updatedAt).slice(11, 19) : '??';
            info = (rp.footprint2D ? '2d' : 'no2d') + ',' + dims + ',' + ts;
          } catch (e2) { info = 'parse-err'; }
          reprs.push(h + '(' + info + ')');
        }
      }
      var vm = '?';
      try {
        var mcRaw = localStorage.getItem('mc:state:v1');
        if (mcRaw) vm = String((JSON.parse(mcRaw) || {}).viewMode || '?');
      } catch (e3) {}
      return 'canvasDiag: blocks=[' + handles.join(',') + '] reprs=[' + reprs.join(',') + '] viewMode=' + vm;
    } catch (e) { return 'canvasDiag: error'; }
  }

  function _debugEnabled() {
    try { return /lpsdebug/.test(global.location.href); } catch (e) { return false; }
  }

  function _initDebugOverlay() {
    if (!_debugEnabled()) return;
    var el = document.createElement('div');
    el.id = 'lps-debug';
    el.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:2147483647;' +
      'background:rgba(0,0,0,.88);color:#9f9;font:11px/1.5 monospace;padding:10px;' +
      'border-radius:8px;max-height:45vh;overflow:auto;white-space:pre-wrap;';
    var extra = { msg: '' };
    var btn = document.createElement('button');
    btn.textContent = 'FORCE SAVE NOW';
    btn.style.cssText = 'display:inline-block;margin:0 8px 8px 0;padding:8px 12px;' +
      'font:12px monospace;background:#5A6B47;color:#fff;border:0;border-radius:6px;';
    btn.addEventListener('click', function () {
      var data = readLocalBasePayload();
      if (data) {
        _pending[pageConfigKey()] = sanitize(data, 0);
        extra.msg = 'FORCE SAVE: sending…';
        sendSave(pageConfigKey()).then(function (res) {
          extra.msg = 'FORCE SAVE result: ' + JSON.stringify(res).slice(0, 300);
        });
      } else {
        extra.msg = 'FORCE SAVE: no local base payload found!';
      }
    });
    var btn2 = document.createElement('button');
    btn2.textContent = 'RELOAD REMOTE';
    btn2.style.cssText = btn.style.cssText;
    btn2.addEventListener('click', function () {
      _loadPromise = null;
      extra.msg = 'RELOAD REMOTE: fetching…';
      loadRemote().then(function (res) {
        extra.msg = 'RELOAD REMOTE result: ' + JSON.stringify(res).slice(0, 300);
      });
    });
    var txt = document.createElement('div');
    el.appendChild(btn);
    el.appendChild(btn2);
    el.appendChild(txt);
    document.body.appendChild(el);
    setInterval(function () {
      var local = readLocalBasePayload();
      var lines = [
        'LevaProjectStore ' + VERSION,
        'identity: ' + getIdentity(),
        'BASE_BOOT.customerId: ' + (global.BASE_BOOT ? JSON.stringify(global.BASE_BOOT.customerId) : 'BASE_BOOT MISSING'),
        'projectId: ' + getProjectId(),
        'hooks: base=' + _baseHooked + ' bridge=' + _bridgeHooked,
        'local base payload: ' + (local ? ('YES (updatedAt ' + local.updatedAt + ')') : 'NONE'),
        'pending saves: ' + (Object.keys(_pending).join(',') || '-'),
        'canvas: ' + (global.MasterCanvas ? ('present, hydrated=' + _canvasHydrated) : 'not on this page'),
        _canvasDiagLine(),
        'lastGlb: ' + JSON.stringify(_lastGlbResult || null).slice(0, 200),
        'lastLoad: ' + JSON.stringify(_lastLoadResult || null).slice(0, 300),
        'lastSave: ' + JSON.stringify(_lastSaveResult || null).slice(0, 300),
        extra.msg
      ];
      txt.textContent = lines.join('\n');
    }, 1500);
  }

  /* ============================================================
     Boot
     ============================================================ */
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  ready(function () {
    dlog('boot', VERSION, 'identity=' + getIdentity());

    loadRemote().then(function (res) {
      reconcileBase(res);
    });

    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var baseDone = hookBaseStore() || !isBasePage();
      var bridgeDone = hookBridge();
      if (baseDone && bridgeDone) { clearInterval(iv); return; }
      if (tries > POLL_MAX_TRIES) {
        clearInterval(iv);
        /* Configurator libs can load lazily (pool starts well after the
           product page renders) — keep trying slowly, forever. */
        dlog('hook polling: slow mode (base=' + _baseHooked + ' bridge=' + _bridgeHooked + ')');
        var iv2 = setInterval(function () {
          var b = hookBaseStore() || !isBasePage();
          var br = hookBridge();
          if (b && br) clearInterval(iv2);
        }, 3000);
      }
    }, POLL_MS);

    setTimeout(syncRepresentations, 8000);
    setInterval(syncRepresentations, REPR_SYNC_INTERVAL_MS);
    handleInspireParam();
    setTimeout(synthesizeBaseRepresentation, 6000);
    setTimeout(checkLocationSources, 7000);
    setInterval(checkLocationSources, 30000);
    pollCanvasHydration();
    loadRemote().then(function () { hydrateCanvas(); });

    global.addEventListener('pagehide', flushAll);

    _initDebugOverlay();
  });

  global.LevaProjectStore = {
    version: VERSION,
    load: loadRemote,
    saveModule: queueSave,
    setLocation: setLocation,
    setPublish: setPublish,
    getIdentity: getIdentity,
    getProjectId: getProjectId,
    getMasterFootprint: getMasterFootprint,
    publishGlb: publishGlbBuffer,
    flush: flushAll,
    diagnose: diagnose
  };

}(window));